import { useMemo, useState } from "react";
import {
  AssessmentGroupListFragmentFragment,
  AssessmentQuestionComment,
  AssessmentQuestionResponseVisibility,
  AssessmentQuestionResponses,
  AssessmentType,
  ComplianceProgramStep,
  OrganizationTalentIndicatorFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";

import { getReviewQuestionScope } from "../review-questions/review-question-rows";
import ReviewTalentIndicatorsRow from "../review-questions/review-talent-indicators-row";
import TalentIndicatorSidebar from "../talent-indicator-sidebar/talent-indicator-sidebar";
import { ProgramForm, ProgramFormStep } from "../types";
import { toQuestionSetOption } from "./question-set-option";
import QuestionSetSelector from "./question-set-selector";
import { QuestionList, QuestionRow } from "./question-tab-section";
import { talentIndicatorsSummary } from "./system-question-summaries";

const UpwardAssessmentStep = ({
  proposedComplianceProgram,
  canUpdate,
  publishedReviewId,
  talentIndicatorData,
  onAddOrUpdateStep,
  availableAssessmentGroups,
  onAssessmentGroupCreated,
  onAssessmentGroupArchived,
  showQuestionSetError,
}: {
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  publishedReviewId: number | null;
  talentIndicatorData?: OrganizationTalentIndicatorFragment[];
  onAddOrUpdateStep: (step: ProgramFormStep) => void;
  availableAssessmentGroups: AssessmentGroupListFragmentFragment[];
  onAssessmentGroupCreated: (
    newAssessmentGroup: AssessmentGroupListFragmentFragment,
  ) => void;
  onAssessmentGroupArchived: (assessmentGroupId: number) => void;
  showQuestionSetError: boolean;
}) => {
  const label = useLabel();

  const [isTalentIndicatorSidebarOpen, setIsTalentIndicatorSidebarOpen] =
    useState(false);
  // Enable opens the sidebar with every indicator switched on, as the switch
  // would.
  const [isEnablingTalentIndicators, setIsEnablingTalentIndicators] =
    useState(false);

  const availableAssessmentGroupSelectOptions = useMemo(() => {
    return availableAssessmentGroups
      .filter(({ assessmentType }) => assessmentType === AssessmentType.Manager)
      .filter((assessmentGroup) => !assessmentGroup.hasNpsQuestions)
      .map(toQuestionSetOption);
  }, [availableAssessmentGroups]);

  const upwardReviewStep = useMemo(() => {
    return proposedComplianceProgram.steps.find(
      (step) => step.stepType === ComplianceProgramStep.UpwardReview,
    );
  }, [proposedComplianceProgram]);

  const selectedAssessmentGroup = useMemo(() => {
    return upwardReviewStep && upwardReviewStep.questionSetIds.length > 0
      ? (availableAssessmentGroups.find(
          ({ id }) => id === upwardReviewStep.questionSetIds[0],
        ) ?? null)
      : null;
  }, [availableAssessmentGroups, upwardReviewStep]);

  const applicableTalentIndicators = useMemo(() => {
    return talentIndicatorData
      ? talentIndicatorData.filter(
          (talentIndicator) => talentIndicator.isManagerIndicator,
        )
      : [];
  }, [talentIndicatorData]);

  const talentIndicatorsWhenEnabled = applicableTalentIndicators.map(
    (talentIndicator) => ({
      talentIndicatorId: talentIndicator.id,
      responses: AssessmentQuestionResponses.ExcludeSelfAssessment,
      responseVisibility:
        AssessmentQuestionResponseVisibility.HiddenFromSubject,
      comment: AssessmentQuestionComment.Optional,
      description: talentIndicator.description,
    }),
  );

  return (
    upwardReviewStep && (
      <>
        {isTalentIndicatorSidebarOpen && (
          <TalentIndicatorSidebar
            onClose={() => {
              setIsTalentIndicatorSidebarOpen(false);
              setIsEnablingTalentIndicators(false);
            }}
            canUpdate={canUpdate}
            assessmentType={AssessmentType.Manager}
            talentIndicatorData={applicableTalentIndicators}
            isEnabling={isEnablingTalentIndicators}
            talentIndicatorSetup={
              isEnablingTalentIndicators
                ? {
                    ...upwardReviewStep.talentIndicatorReviewConfig,
                    talentIndicators: talentIndicatorsWhenEnabled,
                  }
                : upwardReviewStep.talentIndicatorReviewConfig
            }
            onUpdateTalentIndicators={(talentIndicatorReviewConfig) => {
              onAddOrUpdateStep({
                ...upwardReviewStep,
                talentIndicatorReviewConfig,
              });
            }}
          />
        )}
        <QuestionList
          title={`Upward ${label("review", { capitalize: true })}`}
          items={[
            {
              key: "question-set",
              added: !!selectedAssessmentGroup,
              row: (
                <QuestionSetSelector
                  review={getReviewQuestionScope(
                    publishedReviewId,
                    upwardReviewStep,
                  )}
                  assessmentType={AssessmentType.Manager}
                  availableAssessmentGroupSelectOptions={
                    availableAssessmentGroupSelectOptions
                  }
                  canUpdate={canUpdate}
                  onUpdateAssessmentTemplate={(assessmentGroupId) => {
                    onAddOrUpdateStep({
                      ...upwardReviewStep,
                      questionSetIds: assessmentGroupId
                        ? [assessmentGroupId]
                        : [],
                    });
                  }}
                  onAssessmentGroupCreated={onAssessmentGroupCreated}
                  onAssessmentGroupArchived={onAssessmentGroupArchived}
                  selectedAssessmentGroup={selectedAssessmentGroup}
                  error={
                    showQuestionSetError &&
                    upwardReviewStep.questionSetIds.length === 0
                      ? "Please choose a question set"
                      : undefined
                  }
                />
              ),
            },
            {
              key: "talent-indicators",
              added:
                upwardReviewStep.talentIndicatorReviewConfig.talentIndicators
                  .length > 0,
              unavailable: applicableTalentIndicators.length === 0,
              row: (
                <ReviewTalentIndicatorsRow
                  review={getReviewQuestionScope(
                    publishedReviewId,
                    upwardReviewStep,
                  )}
                  title="Manager Talent Indicators"
                  summary={talentIndicatorsSummary(
                    upwardReviewStep.talentIndicatorReviewConfig,
                    applicableTalentIndicators,
                  )}
                >
                  <QuestionRow
                    title="Manager Talent Indicators"
                    summary={talentIndicatorsSummary(
                      upwardReviewStep.talentIndicatorReviewConfig,
                      applicableTalentIndicators,
                    )}
                    warning={
                      applicableTalentIndicators.length === 0
                        ? "There was a problem loading talent indicators"
                        : undefined
                    }
                    warningSummary="Talent indicators could not be loaded"
                    added={
                      upwardReviewStep.talentIndicatorReviewConfig
                        .talentIndicators.length > 0
                    }
                    disabled={!canUpdate}
                    onRemove={() =>
                      onAddOrUpdateStep({
                        ...upwardReviewStep,
                        talentIndicatorReviewConfig: {
                          ...upwardReviewStep.talentIndicatorReviewConfig,
                          talentIndicators: [],
                        },
                      })
                    }
                    onEdit={() => setIsTalentIndicatorSidebarOpen(true)}
                    onAdd={() => {
                      setIsEnablingTalentIndicators(true);
                      setIsTalentIndicatorSidebarOpen(true);
                    }}
                  />
                </ReviewTalentIndicatorsRow>
              ),
            },
          ]}
        />
      </>
    )
  );
};
export default UpwardAssessmentStep;
